import React, { useCallback, useEffect, useMemo, useState } from 'react'
import loadable from '@loadable/component'
import { get } from 'lodash'
import { Box, Typography } from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import {
  formatDollarsWithSymbol,
  formatPercentage,
  formatWithCommas,
} from '../../utils/functions'
import AsOfDate from '../AsOfDate'
import OutlinedDropdown from '../Common/Dropdowns/OutlinedDropdown'
import FourColumnsDatapoints from '../FourColumnsDatapoints'
import ScrollingTable from '../Tables/ScrollingTable'

const LoadableHistoricalPremiumDiscountChart = loadable(
  () => import('./HistoricalPremiumDiscountChart'),
  {
    ssr: false,
    fallback: <Typography variant="body1">Loading chart...</Typography>,
  }
)

const TableContainer = styled('div')(({ theme }) => ({
  marginTop: '64px',
  [theme.breakpoints.down('xs')]: { marginTop: '40px' },
}))

const HistoricalPremiumDiscountTitle = styled(Typography)(({ theme }) => ({
  marginTop: '120px',
  marginBottom: '40px',
  fontWeight: 500,
  fontSize: '20px',
  lineHeight: '32px',
  [theme.breakpoints.down('xs')]: {
    fontSize: '24px',
    lineHeight: '36px',
    width: '339px',
    marginTop: '40px',
  },
}))

const Gap = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: 0,
  height: '23px',
  backgroundColor: '#FFFFFF',
  zIndex: 12,
  [theme.breakpoints.down('sm')]: { display: 'none' },
}))

const boxStyles = { maxWidth: '204px' }

export default function EtfPricingTrading({ data = {}, disclaimer = {} }) {
  const theme = useTheme()

  const pricingTrading = get(data, 'etfMarketValuation') || {}
  const historicalPremiumDiscount = get(data, 'etfHistoricalMarketValuations') || []
  const premiumDiscountDisclaimer = disclaimer.html || ''

  const safeFormat = (val, fn) => (val ? fn(val) : '--')

  const firstRowDatapoints = useMemo(
    () => [
      {
        value: safeFormat(pricingTrading.bidAskMidPrice, formatDollarsWithSymbol),
        label: 'Bid/Ask Mid Price',
      },
      {
        value: safeFormat(pricingTrading.marketPrice, formatDollarsWithSymbol),
        label: 'Closing Market Price',
      },
      {
        value: safeFormat(pricingTrading.premiumDiscount, formatDollarsWithSymbol),
        label: 'Premium/Discount ($)',
      },
      {
        value: safeFormat(pricingTrading.premiumDiscountPercent, formatPercentage),
        label: 'Premium/Discount (%)',
      },
    ],
    [pricingTrading]
  )

  const secondRowDatapoints = useMemo(
    () => [
      {
        value: safeFormat(pricingTrading.medianBidAsk30DaySpread, formatPercentage),
        label: '30-Day Median Bid/Ask Spread',
      },
      {
        value: safeFormat(pricingTrading.exchangeVolume, formatWithCommas),
        label: 'Exchange Volume (Shares)',
      },
      { value: 'USD', label: 'Trading Currency' },
    ],
    [pricingTrading]
  )

  const premiumDiscounts = useMemo(() => {
    const ytdArr = get(data, 'etfMarketValuation.premiumDiscountYtd') || []
    const lastYearArr = get(data, 'etfMarketValuation.premiumDiscountLastyear') || []
    const quartersArr = get(data, 'etfMarketValuation.premiumDiscountQuarterly') || []
    const sinceIncpArr = get(data, 'etfMarketValuation.premiumDiscountSinceInception') || []

    return {
      ytd: ytdArr[0] || {},
      lastyear: lastYearArr[0] || {},
      quarters: quartersArr,
      incp: sinceIncpArr[0] || {},
      historicalPremiumDiscount,
      asOfDate: get(data, 'etfMarketValuation.calendarDate') || null,
    }
  }, [data, historicalPremiumDiscount])

  const premiumDiscountTableColumns = useMemo(() => {
    const cols = [{ id: 'label', Header: '', accessor: 'label', firstColumn: true }]

    if (premiumDiscounts.lastyear?.yearNumber) {
      cols.push({
        id: 'lastYear',
        Header: premiumDiscounts.lastyear?.yearNumber,
        accessor: 'lastYear',
      })
    }

    premiumDiscounts.quarters?.forEach((qtr) => {
      cols.push({
        id: `q${qtr.quarterNumber}_${qtr.yearNumber}`,
        Header: `Q${qtr.quarterNumber} ${qtr.yearNumber}`,
        accessor: `q${qtr.quarterNumber}_${qtr.yearNumber}`,
      })
    })

    if (premiumDiscounts.incp?.daysAtPremium) {
      cols.push({ id: 'sinceIncp', Header: 'Since Incp.', accessor: 'sinceIncp' })
    } else {
      cols.push({ id: 'ytd', Header: 'YTD', accessor: 'ytd' })
    }

    return cols
  }, [
    premiumDiscounts.lastyear?.yearNumber,
    premiumDiscounts.quarters,
    premiumDiscounts.incp?.daysAtPremium,
  ])

  const premiumDiscountTableData = useMemo(() => {
    const premiumRow = { label: 'Days Traded at a Premium' }
    const discountRow = { label: 'Days Traded at a Discount' }

    if (premiumDiscounts.lastyear?.yearNumber) {
      premiumRow.lastYear = premiumDiscounts.lastyear?.daysAtPremium || '--'
      discountRow.lastYear = premiumDiscounts.lastyear?.daysAtDiscount || '--'
    }

    premiumDiscounts.quarters?.forEach((qtr) => {
      premiumRow[`q${qtr.quarterNumber}_${qtr.yearNumber}`] = qtr.daysAtPremium || '--'
      discountRow[`q${qtr.quarterNumber}_${qtr.yearNumber}`] = qtr.daysAtDiscount || '--'
    })

    if (premiumDiscounts.incp?.daysAtPremium) {
      premiumRow.sinceIncp = premiumDiscounts.incp?.daysAtPremium || '--'
      discountRow.sinceIncp = premiumDiscounts.incp?.daysAtDiscount || '--'
    } else {
      premiumRow.ytd = premiumDiscounts.ytd?.daysAtPremium || '--'
      discountRow.ytd = premiumDiscounts.ytd?.daysAtDiscount || '--'
    }

    return [premiumRow, discountRow]
  }, [premiumDiscounts])

  const separatedGraphData = useMemo(() => {
    const arr = []
    const currYear = new Date().getFullYear()
    const prevYear = currYear - 1

    premiumDiscounts.historicalPremiumDiscount?.forEach((dayData) => {
      const { yearNumber, quarterNumber } = dayData.calendar

      if (yearNumber === prevYear) {
        let entry = arr.find((e) => e.labelValue === prevYear)
        if (!entry) {
          entry = { labelValue: prevYear, data: [dayData], label: `${prevYear}` }
          arr.push(entry)
        } else {
          entry.data.push(dayData)
        }
      }

      if (yearNumber === currYear) {
        let qEntry = arr.find((e) => e.labelValue === quarterNumber)
        if (!qEntry) {
          qEntry = {
            labelValue: quarterNumber,
            data: [dayData],
            label: `Q${quarterNumber} ${yearNumber}`,
          }
          arr.push(qEntry)
        } else {
          qEntry.data.push(dayData)
        }

        let ytdEntry = arr.find((e) => e.label === 'YTD')
        if (!ytdEntry) {
          ytdEntry = { labelValue: 12, data: [dayData], label: 'YTD' }
          arr.push(ytdEntry)
        } else {
          ytdEntry.data.push(dayData)
        }
      }
    })

    return arr
  }, [premiumDiscounts.historicalPremiumDiscount])

  const [lineGraphComponentData, setLineGraphComponentData] = useState({
    empty: true,
    emptyMessage: 'No Data Available',
  })
  const [graphOptions, setGraphOptions] = useState([])
  const [selectedGraphObject, setSelectedGraphObject] = useState({})

  useEffect(() => {
    if (separatedGraphData.length) {
      const opts = separatedGraphData
        .map((d) => ({ id: d.label, title: d.label }))
        .sort((a, b) => a.title.localeCompare(b.title))

      setGraphOptions(opts)

      const defaultItem =
        separatedGraphData.find((i) => i.label === 'YTD') ||
        separatedGraphData.find(
          (i) => i.label === `${new Date().getFullYear() - 1}`
        )

      if (defaultItem) {
        setLineGraphComponentData({
          empty: false,
          key: defaultItem.labelValue,
          label: defaultItem.label,
          data: defaultItem.data,
        })
        setSelectedGraphObject(defaultItem)
      }
    }
  }, [separatedGraphData])

  const handleGraphChange = useCallback(
    (e) => {
      const sel = separatedGraphData.find((d) => d.label === e.target.value)
      if (sel) {
        setLineGraphComponentData({
          empty: false,
          key: sel.labelValue,
          label: sel.label,
          data: sel.data,
        })
        setSelectedGraphObject(sel)
      } else {
        setLineGraphComponentData({
          empty: true,
          emptyMessage: 'No Data Available',
        })
      }
    },
    [separatedGraphData]
  )

  const columnWidth = `${100 / premiumDiscountTableColumns.length}%`
  const dropdownStyles = useMemo(
    () => ({ contrast: theme.palette.primary.base }),
    [theme]
  )

  return (
    <>
      <AsOfDate date={premiumDiscounts.asOfDate} />
      <FourColumnsDatapoints datapoints={firstRowDatapoints} />
      {premiumDiscountDisclaimer && (
        <Typography color="primary" variant="subtitle2">
          {premiumDiscountDisclaimer}
        </Typography>
      )}
      <Gap />
      <TableContainer>
        <ScrollingTable
          data={premiumDiscountTableData}
          columns={premiumDiscountTableColumns}
          Col1Width={columnWidth}
        />
      </TableContainer>
      <FourColumnsDatapoints datapoints={secondRowDatapoints} />
      <HistoricalPremiumDiscountTitle>
        Historical Premium Discount to NAV
      </HistoricalPremiumDiscountTitle>
      <Box sx={boxStyles}>
        {graphOptions.length > 0 && (
          <OutlinedDropdown
            id="historicalPremiumDiscount"
            hasTitle={false}
            options={graphOptions}
            handleChange={handleGraphChange}
            selected={selectedGraphObject.label}
            styles={dropdownStyles}
          />
        )}
      </Box>
      {lineGraphComponentData.empty ? (
        <Typography>{lineGraphComponentData.emptyMessage}</Typography>
      ) : (
        <div label={lineGraphComponentData.label} key={lineGraphComponentData.key}>
          <LoadableHistoricalPremiumDiscountChart
            data={lineGraphComponentData.data}
          />
        </div>
      )}
    </>
  )
}
